import React, { useState, useEffect, useMemo } from 'react';
import {
	Modal,
	ModalOverlay,
	ModalContent,
	ModalBody,
	Button,
	Text,
	VStack,
	HStack,
	Badge,
	Box,
	useToast,
	Spinner,
	Alert,
	AlertIcon,
	IconButton,
	Icon,
	Flex,
	SimpleGrid,
	Tabs,
	TabList,
	Tab,
	TabPanels,
	TabPanel,
	Skeleton,
	Divider,
} from '@chakra-ui/react';
import {
	FaBolt,
	FaCalendarPlus,
	FaArrowLeft,
	FaEnvelope,
	FaCheckCircle,
	FaShieldAlt,
} from 'react-icons/fa';
import { Elements } from '@stripe/react-stripe-js';
import getStripePromise from '../../utils/stripePromise';
import {
	useEmailPackProducts,
	useEmailPackPaymentIntent,
	useConfirmEmailPackPurchase,
} from '../../hooks/useEmailPacks';
import { useAuthenticatedUserInfo } from '../../services/Contexts/AuthenticatedUserInfoContext';
import PaymentForm from '../Auth/PaymentForm';

const stripePromise = getStripePromise();

const formatPrice = (amount, currency = 'USD', { suffix = '' } = {}) => {
	if (amount == null) return '';
	const symbol = currency === 'USD' ? '$' : `${currency} `;
	const decimals = currency === 'USD' ? 0 : 2;
	return `${symbol}${Number(amount).toFixed(decimals)}${suffix}`;
};

const formatEmails = (count) => `+${Number(count).toLocaleString()}`;

const accentForType = (type) =>
	type === 'monthly'
		? { scheme: 'teal', gradient: 'linear(135deg, teal.400 0%, cyan.500 100%)', icon: FaCalendarPlus }
		: { scheme: 'orange', gradient: 'linear(135deg, orange.400 0%, yellow.500 100%)', icon: FaBolt };

const ProductCardSkeleton = () => (
	<Box p={5} borderRadius='xl' border='1px solid' borderColor='gray.100' bg='white'>
		<Skeleton height='40px' width='40px' borderRadius='full' mb={4} />
		<Skeleton height='32px' width='70%' mb={2} />
		<Skeleton height='14px' width='50%' mb={4} />
		<Skeleton height='12px' width='100%' mb={1} />
		<Skeleton height='12px' width='80%' mb={5} />
		<Skeleton height='40px' borderRadius='lg' />
	</Box>
);

const ProductCard = ({ product, isLoading, onSelect, isRecommended }) => {
	const accent = accentForType(product.type);
	const priceLabel =
		product.type === 'monthly'
			? formatPrice(product.recommendedPriceUsd, 'USD', { suffix: '/mo' })
			: formatPrice(product.recommendedPriceUsd, 'USD');

	return (
		<Box
			as='button'
			type='button'
			onClick={() => onSelect(product)}
			disabled={isLoading}
			position='relative'
			textAlign='left'
			w='full'
			p={5}
			borderRadius='xl'
			border='2px solid'
			borderColor='gray.100'
			bg='white'
			boxShadow='sm'
			transition='all 0.2s ease'
			_hover={
				isLoading
					? undefined
					: {
							borderColor: `${accent.scheme}.300`,
							boxShadow: 'md',
							transform: 'translateY(-2px)',
						}
			}
			_focusVisible={{ outline: 'none', borderColor: `${accent.scheme}.400`, boxShadow: '0 0 0 3px var(--chakra-colors-teal-100)' }}
			opacity={isLoading ? 0.85 : 1}
			cursor={isLoading ? 'wait' : 'pointer'}>
			{isRecommended && (
				<Badge
					position='absolute'
					top={3}
					right={3}
					colorScheme={accent.scheme}
					borderRadius='full'
					px={2}
					fontSize='10px'
					textTransform='uppercase'
					letterSpacing='wider'>
					Popular
				</Badge>
			)}

			<Flex
				align='center'
				justify='center'
				w={10}
				h={10}
				borderRadius='full'
				bgGradient={accent.gradient}
				mb={4}
				color='white'>
				<Icon as={accent.icon} boxSize={4} />
			</Flex>

			<Text fontSize='3xl' fontWeight='800' lineHeight='1' color='gray.800' mb={1}>
				{formatEmails(product.emails)}
			</Text>
			<Text fontSize='xs' color='gray.500' fontWeight='600' textTransform='uppercase' letterSpacing='0.08em' mb={3}>
				{product.type === 'monthly' ? 'emails every month' : 'emails this billing month'}
			</Text>

			<Text fontWeight='700' fontSize='md' color='gray.800' mb={1} noOfLines={1}>
				{product.name}
			</Text>
			<Text fontSize='sm' color='gray.500' mb={4} noOfLines={3} minH='2.5rem'>
				{product.description}
			</Text>

			<Divider mb={4} />

			<Flex align='center' justify='space-between' gap={3}>
				<Box>
					<Text fontSize='2xl' fontWeight='800' color={`${accent.scheme}.600`} lineHeight='1'>
						{priceLabel}
					</Text>
					<Text fontSize='xs' color='gray.400' mt={1}>
						{product.type === 'monthly' ? 'Billed monthly' : 'One-time charge'}
					</Text>
				</Box>
				<Button
					as='span'
					colorScheme={accent.scheme}
					size='sm'
					px={5}
					isLoading={isLoading}
					pointerEvents='none'
					flexShrink={0}>
					{product.type === 'monthly' ? 'Subscribe' : 'Buy now'}
				</Button>
			</Flex>
		</Box>
	);
};

const EmailPackPurchaseModal = ({
	isOpen,
	onClose,
	onPurchaseSuccess,
	title = 'Buy more emails',
	description = 'Add monthly send packs or one-time top-ups. Top-ups expire when your monthly counter resets.',
}) => {
	const toast = useToast();
	const { companyId } = useAuthenticatedUserInfo();
	const { data: products = [], isLoading } = useEmailPackProducts();
	const createPayment = useEmailPackPaymentIntent();
	const confirmPurchase = useConfirmEmailPackPurchase(companyId);

	const [pendingKey, setPendingKey] = useState(null);
	const [paymentSession, setPaymentSession] = useState(null);
	const [activeTab, setActiveTab] = useState(0);

	const monthlyProducts = useMemo(
		() => products.filter((p) => p.type === 'monthly'),
		[products],
	);
	const topUpProducts = useMemo(
		() => products.filter((p) => p.type === 'topup'),
		[products],
	);

	const showTabs = monthlyProducts.length > 0 && topUpProducts.length > 0;
	const defaultTab = monthlyProducts.length > 0 ? 0 : 1;

	useEffect(() => {
		if (!isOpen) {
			setPaymentSession(null);
			setPendingKey(null);
			setActiveTab(defaultTab);
		}
	}, [isOpen, defaultTab]);

	const resetPaymentState = () => {
		setPaymentSession(null);
		setPendingKey(null);
	};

	const handleClose = () => {
		resetPaymentState();
		onClose();
	};

	const handleSelectProduct = async (product) => {
		try {
			setPendingKey(product.key);
			const session = await createPayment.mutateAsync({
				companyId,
				packKey: product.key,
			});

			if (!session?.clientSecret) {
				throw new Error('Payment could not be started.');
			}

			setPaymentSession(session);
		} catch (error) {
			toast({
				title: 'Payment setup failed',
				description: error?.response?.data?.error || error.message,
				status: 'error',
				duration: 6000,
				isClosable: true,
			});
		} finally {
			setPendingKey(null);
		}
	};

	const handlePaymentSuccess = async (stripeResult) => {
		try {
			const isTopUp = paymentSession.type === 'topup';
			const result = await confirmPurchase.mutateAsync({
				companyId,
				packKey: paymentSession.packKey,
				paymentIntentId: isTopUp ? stripeResult.id : undefined,
				setupIntentId: !isTopUp ? stripeResult.id : undefined,
				subscriptionId: paymentSession.subscriptionId,
			});

			const productName = paymentSession.productName || 'Email pack';

			toast({
				title: isTopUp ? 'Top-up complete' : 'Subscription started',
				description: isTopUp
					? `${productName} has been added. Your send limit is updated.`
					: `${productName} is now active. Your monthly send limit is updated.`,
				status: 'success',
				duration: 6000,
				isClosable: true,
			});

			resetPaymentState();
			onPurchaseSuccess?.(result);
			onClose();
		} catch (error) {
			toast({
				title: 'Could not apply purchase',
				description: error?.response?.data?.error || error.message,
				status: 'error',
				duration: 6000,
				isClosable: true,
			});
		}
	};

	const paymentButtonText = paymentSession
		? paymentSession.type === 'monthly'
			? `Subscribe ${formatPrice(paymentSession.amount, paymentSession.currency)}/mo`
			: `Pay ${formatPrice(paymentSession.amount, paymentSession.currency)}`
		: '';

	const checkoutAccent = paymentSession ? accentForType(paymentSession.type) : null;

	const renderProductGrid = (items, recommendedKey) => (
		<SimpleGrid columns={{ base: 1, md: items.length > 1 ? 2 : 1 }} spacing={4}>
			{items.map((product) => (
				<ProductCard
					key={product.key}
					product={product}
					isLoading={pendingKey === product.key}
					onSelect={handleSelectProduct}
					isRecommended={product.key === recommendedKey}
				/>
			))}
		</SimpleGrid>
	);

	return (
		<Modal
			isOpen={isOpen}
			onClose={handleClose}
			size={{ base: 'full', md: '2xl' }}
			isCentered
			scrollBehavior='inside'
			closeOnOverlayClick={!paymentSession}>
			<ModalOverlay bg='blackAlpha.600' backdropFilter='blur(4px)' />
			<ModalContent
				mx={4}
				my={{ base: 0, md: 4 }}
				borderRadius={{ base: 0, md: '2xl' }}
				overflow='hidden'
				maxH={{ base: '100vh', md: '90vh' }}>
				{paymentSession ? (
					<>
						<Box
							bgGradient='linear(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)'
							px={6}
							py={5}
							color='white'
							position='relative'>
							<Box
								position='absolute'
								top='-40px'
								right='-40px'
								w='140px'
								h='140px'
								borderRadius='full'
								bg='whiteAlpha.100'
								pointerEvents='none'
							/>
							<HStack spacing={3} position='relative' zIndex={1} pr={10}>
								<IconButton
									aria-label='Back to packs'
									icon={<FaArrowLeft />}
									size='sm'
									variant='ghost'
									color='white'
									_hover={{ bg: 'whiteAlpha.200' }}
									onClick={resetPaymentState}
								/>
								<Box flex={1}>
									<Text fontSize='xs' color='whiteAlpha.700' fontWeight='600' textTransform='uppercase' letterSpacing='0.1em' mb={1}>
										Secure checkout
									</Text>
									<Text fontWeight='800' fontSize='xl' m={0}>
										{paymentSession.productName}
									</Text>
								</Box>
							</HStack>
							<IconButton
								aria-label='Close'
								icon={<Text fontSize='lg' lineHeight={1}>×</Text>}
								position='absolute'
								top={3}
								right={3}
								variant='ghost'
								color='white'
								_hover={{ bg: 'whiteAlpha.200' }}
								onClick={handleClose}
							/>
						</Box>

						<ModalBody px={6} py={5}>
							<VStack align='stretch' spacing={5}>
								<Box
									p={4}
									borderRadius='xl'
									border='1px solid'
									borderColor='gray.100'
									bg='gray.50'>
									<HStack justify='space-between' align='flex-start' mb={3}>
										<HStack align='flex-start' spacing={3}>
											<Flex
												align='center'
												justify='center'
												w={9}
												h={9}
												borderRadius='lg'
												bgGradient={checkoutAccent.gradient}
												color='white'
												flexShrink={0}>
												<Icon as={checkoutAccent.icon} boxSize={4} />
											</Flex>
											<Box>
												<Text fontWeight='700' fontSize='sm' color='gray.800' m={0}>
													{paymentSession.productName}
												</Text>
												<Text fontSize='xs' color='gray.500' m={0} mt={0.5}>
													{paymentSession.type === 'monthly'
														? 'Recurring monthly add-on'
														: 'One-time top-up for this billing month'}
												</Text>
											</Box>
										</HStack>
										<Badge colorScheme={checkoutAccent.scheme} borderRadius='full' px={2}>
											{paymentSession.type === 'monthly' ? 'Monthly' : 'Top-up'}
										</Badge>
									</HStack>
									<Flex justify='space-between' align='center'>
										<Text fontSize='sm' color='gray.600' m={0}>
											Total due today
										</Text>
										<Text fontSize='xl' fontWeight='800' color={`${checkoutAccent.scheme}.600`} m={0}>
											{paymentSession.type === 'monthly'
												? `${formatPrice(paymentSession.amount, paymentSession.currency)}/mo`
												: formatPrice(paymentSession.amount, paymentSession.currency)}
										</Text>
									</Flex>
								</Box>

								<Text fontSize='sm' color='gray.600' m={0}>
									{paymentSession.type === 'monthly'
										? 'Save your payment method to start the monthly add-on. You will be charged when you confirm.'
										: 'Your send allowance updates immediately after payment.'}
								</Text>

								<Box
									border='1px solid'
									borderColor='gray.100'
									borderRadius='xl'
									overflow='hidden'
									bg='white'>
									<Elements
										stripe={stripePromise}
										options={{
											clientSecret: paymentSession.clientSecret,
											appearance: {
												theme: 'stripe',
												variables: {
													colorPrimary: paymentSession.type === 'monthly' ? '#319795' : '#DD6B20',
													borderRadius: '8px',
												},
											},
										}}>
										<Box sx={{ '& form': { padding: '1.25rem !important' } }}>
											<PaymentForm
												amount={paymentSession.amount}
												currency={paymentSession.currency}
												mode={paymentSession.paymentMode || (paymentSession.type === 'monthly' ? 'setup' : 'payment')}
												buttonText={paymentButtonText}
												onSuccess={handlePaymentSuccess}
											/>
										</Box>
									</Elements>
								</Box>

								<HStack spacing={2} justify='center' color='gray.400' pt={1}>
									<Icon as={FaShieldAlt} boxSize={3} />
									<Text fontSize='xs' m={0}>
										Payments secured by Stripe
									</Text>
								</HStack>
							</VStack>
						</ModalBody>
					</>
				) : (
					<>
						<Box
							bgGradient='linear(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)'
							px={6}
							py={6}
							color='white'
							position='relative'
							overflow='hidden'>
							<Box
								position='absolute'
								top='-50px'
								right='-50px'
								w='180px'
								h='180px'
								borderRadius='full'
								bg='whiteAlpha.100'
								pointerEvents='none'
							/>
							<Box
								position='absolute'
								bottom='-30px'
								left='40%'
								w='100px'
								h='100px'
								borderRadius='full'
								bg='whiteAlpha.50'
								pointerEvents='none'
							/>
							<HStack spacing={4} position='relative' zIndex={1} pr={10} align='flex-start'>
								<Flex
									align='center'
									justify='center'
									w={12}
									h={12}
									borderRadius='xl'
									bg='whiteAlpha.200'
									flexShrink={0}>
									<Icon as={FaEnvelope} boxSize={5} color='teal.200' />
								</Flex>
								<Box>
									<Text fontSize='xs' color='whiteAlpha.700' fontWeight='600' textTransform='uppercase' letterSpacing='0.12em' mb={1}>
										Email packs & add-ons
									</Text>
									<Text as='h2' fontSize={{ base: 'xl', md: '2xl' }} fontWeight='800' lineHeight='1.2' m={0} mb={2}>
										{title}
									</Text>
									<Text fontSize='sm' color='whiteAlpha.800' m={0} maxW='lg'>
										{description}
									</Text>
								</Box>
							</HStack>
							<IconButton
								aria-label='Close'
								icon={<Text fontSize='lg' lineHeight={1}>×</Text>}
								position='absolute'
								top={3}
								right={3}
								variant='ghost'
								color='white'
								_hover={{ bg: 'whiteAlpha.200' }}
								onClick={handleClose}
							/>
						</Box>

						<ModalBody px={6} py={5}>
							{isLoading ? (
								<SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
									<ProductCardSkeleton />
									<ProductCardSkeleton />
								</SimpleGrid>
							) : products.length === 0 ? (
								<Alert status='info' borderRadius='xl' py={4}>
									<AlertIcon />
									<Box>
										<Text fontWeight='600' fontSize='sm' m={0}>
											No packs available
										</Text>
										<Text fontSize='sm' m={0} mt={1}>
											Email packs are not configured yet. Contact support or upgrade your plan.
										</Text>
									</Box>
								</Alert>
							) : showTabs ? (
								<Tabs
									index={activeTab}
									onChange={setActiveTab}
									variant='soft-rounded'
									colorScheme='teal'
									isLazy>
									<TabList bg='gray.100' p={1} borderRadius='xl' mb={5}>
										<Tab
											flex={1}
											fontWeight='600'
											fontSize='sm'
											_selected={{ bg: 'white', boxShadow: 'sm' }}>
											<HStack spacing={2}>
												<Icon as={FaCalendarPlus} boxSize={3} />
												<Text>Monthly add-ons</Text>
											</HStack>
										</Tab>
										<Tab
											flex={1}
											fontWeight='600'
											fontSize='sm'
											_selected={{ bg: 'white', boxShadow: 'sm' }}>
											<HStack spacing={2}>
												<Icon as={FaBolt} boxSize={3} />
												<Text>One-time top-ups</Text>
											</HStack>
										</Tab>
									</TabList>
									<TabPanels px={0}>
										<TabPanel px={0} pt={0}>
											{renderProductGrid(monthlyProducts, 'addon_10k')}
										</TabPanel>
										<TabPanel px={0} pt={0}>
											{renderProductGrid(topUpProducts, 'topup_5k')}
										</TabPanel>
									</TabPanels>
								</Tabs>
							) : monthlyProducts.length > 0 ? (
								renderProductGrid(monthlyProducts, 'addon_10k')
							) : (
								renderProductGrid(topUpProducts, 'topup_5k')
							)}

							{!isLoading && products.length > 0 && (
								<HStack
									mt={6}
									p={3}
									spacing={3}
									borderRadius='lg'
									bg='blue.50'
									border='1px solid'
									borderColor='blue.100'
									align='flex-start'>
									<Icon as={FaCheckCircle} color='blue.500' mt={0.5} flexShrink={0} />
									<Text fontSize='xs' color='blue.800' m={0} lineHeight='1.5'>
										Monthly add-ons renew with your billing cycle and stack with your plan allowance.
										One-time top-ups apply immediately and expire when your monthly send counter resets.
									</Text>
								</HStack>
							)}
						</ModalBody>
					</>
				)}
			</ModalContent>
		</Modal>
	);
};

export default EmailPackPurchaseModal;
